import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import {
    bluishWhiteShade,
    challanCancelDisabledBgColor,
    challanCancelIconBgColor,
    challanCancelIconColor,
    challanEditDisabledBgColor,
    challanEditDisabledBorderColor,
    challanEditIconBgColor,
    challanEditIconColor,
    challanExcelIconBgColor,
    challanExcelIconColor,
    challanSettlementDisabledBgColor,
    challanSettlementDisabledBorderColor,
    grayShadeWithSoftBlue,
    metricBlueColor,
    metricGreenColor,
    tagBlueBgColor
} from "../../../config/ColorObj";
import endpoint from "../../../config/endpoints";
import {
    activeChallanCanBeSettle,
    areYouSureMsg,
    assignedMsg,
    beatMsg,
    cancelChallan,
    cancelledChallanMsg,
    challanCancelMsg,
    challanLifeCycleTimelineMsg,
    challanSettlement,
    createdMsg,
    deleteChallan,
    deletemsg,
    deleteWarningMsg,
    downloadCSV,
    editChallan,
    loadingDetailsMsg,
    noDetailsAvailableForThisChallanMsg,
    pendingChallanCanBeCancel,
    pendingChallanCanBeDelete,
    pendingChallanCanBeEdit,
    serverMsg
} from "../../../config/messageconstant";
import { ChallanDetailsJson } from "../../../DynamicFormsJson/MastersJSON/ChallanDetails";
import { apiDelete, apiGet } from "../../../utils/api_service";
import { showLoador, showNotification } from "../../Landing/Landing.slice";
import { downloadF } from "../../PDF/PDFDownload";
import { Tag, TAG_ICONS } from "../helpers/Helpers";
import {
    challanCard,
    metrics as mStyles,
    sectionLabel,
} from "../styles/ChallanStyles";
import { setSettlementPrefetch } from "../../ChallanSettlement/settlementPrefetchCache";
import { DetailCard } from "./DetailCard";
import { FlagStrip } from "./FlagStrip";
import { MetricCard } from "./MetricCard";
import { ProductsTable } from "./ProductsTable";
import { BillIcon, CancelIcon, ChevronIcon, DeleteIcon, EditIcon, ExcelIcon } from "./SvgIcons";
import { Timeline } from "./Timeline";


// ── Breakpoint Hook ───────────────────────────────────────────────────────────
const getBreakpoint = () => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    return w < 600 ? "mobile" : w < 900 ? "tablet" : "desktop";
};

const useBreakpoint = () => {
    const [bp, setBp] = useState(getBreakpoint);
    useEffect(() => {
        const handler = () => setBp(getBreakpoint());
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);
    return bp;
};

// ── Stat builder ──────────────────────────────────────────────────────────────
const getStats = (challan) => [
    { value: challan.dayDifference + " Days", label: "Days Open", color: challan.dayDifferenceColor },
    { value: challan.revenue, label: "Bill Revenue", color: challan.revenueColor },
    { value: challan.bills, label: "Bills gen.", color: challan.blueShadeColor },
    { value: challan.progress + " %", label: "Progress", color: challan.progressColor },
];

// ── Component ─────────────────────────────────────────────────────────────────
const ChallanCard = ({
    key,
    challan,
    onExpand,
    handleSearchData,
    showLoador,
    showNotification,
    lastSearchParams,
}) => {

    const navigate = useNavigate();
    const bp = useBreakpoint();

    const [open, setOpen] = useState(false);
    const [bodyDetail, setBodyDetail] = useState(null);
    const [loadingBody, setLoadingBody] = useState(false);

    const fetchedRef = useRef(false);

    const isMobile = bp === "mobile";

    // ── Toggle + fetch on first open ─────────────────────────────────────────
    const toggleOpen = useCallback(async () => {
        const opening = !open;
        setOpen(opening);

        if (opening && !fetchedRef.current && typeof onExpand === "function") {
            fetchedRef.current = true;
            setLoadingBody(true);
            const detail = await onExpand(challan.rawId);
            setLoadingBody(false);
            if (detail) {
                console.log("detail-", detail)
                setBodyDetail(detail);
            }
        }
    }, [open, onExpand, challan.rawId]);

    const rowCancel = async () => {
        swal({
            title: areYouSureMsg,
            text: challanCancelMsg,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                showLoador({ loador: true });
                const statusCode = 2;
                await apiGet({
                    url:
                        endpoint.vanStockTransferHeader +
                        "/update-status?vanStockTransferHeaderId=" +
                        challan.rawId +
                        "&status=" +
                        statusCode,
                }).then(async ({ data, success }) => {
                    showLoador({ loador: false });
                    if (!success) {
                        showNotification({
                            msg: serverMsg,
                            severity: "error",
                        });
                    } else {
                        showNotification({ msg: cancelledChallanMsg });
                        await handleSearchData(lastSearchParams.current);
                    }
                });
            }
        });
    };

    const rowDelete = async () => {
        swal({
            title: areYouSureMsg,
            text: deleteWarningMsg,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                showLoador({ loador: true });
                await apiDelete({
                    url: endpoint.vanStockTransferHeader + "/" + challan.rawId
                }).then(async ({ data, success }) => {
                    if (!success) {
                        showNotification({
                            msg: serverMsg,
                            severity: "error",
                        });
                    } else {
                        showNotification({ msg: data?.data?.message || deletemsg });
                        await handleSearchData(lastSearchParams.current);
                    }
                });
                showLoador({ loador: false });
            }
        });
    };

    const rowExcelDownload = async () => {

        showLoador({ loador: true });
        await downloadF({
            isGet: true,
            url:
                endpoint.reportForExcelReports +
                "/challan-data-by-challan-id?challanId=" + challan.rawId,
            ext: "xls",
            openNewTab: true,
        }).then(({ response, success }) => {
            showLoador({ loador: false });
        });
    };

    const handleChallanClick = useCallback(
        (e) => {
            e.stopPropagation();
            setSettlementPrefetch(
                challan.rawId,
                apiGet({
                    url:
                        endpoint.vanStockTransferHeader +
                        "/day-end-details-by-driver-for-challan?challanId=" +
                        challan.rawId,
                }),
            );
            navigate("/challan-settlement/" + challan.rawId);
        },
        [navigate, challan.rawId],
    );

    const handleEditClick = useCallback(
        (e) => {
            e.stopPropagation();
            navigate(ChallanDetailsJson.formPath + "/" + challan.rawId);
        },
        [navigate, challan.rawId],
    );

    const stopPropagation = useCallback((e) => e.stopPropagation(), []);

    // ── Memoized styles ───────────────────────────────────────────────────────
    const headerStyle = useMemo(
        () => ({
            ...challanCard.header(open),
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
        }),
        [open, isMobile],
    );

    const headerCenterStyle = useMemo(
        () => ({
            ...challanCard.headerCenter,
            ...(isMobile && { flex: "1 1 100%", padding: 0 }),
        }),
        [isMobile],
    );

    const stats = useMemo(() => getStats(challan), [challan]);

    const metricGridStyle = useMemo(
        () => ({
            ...mStyles.grid,
            gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fill, minmax(195px, 2fr))",
        }),
        [isMobile],
    );

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderStats = useMemo(
        () =>
            stats.map((s, i) => (
                <div
                    key={i}
                    style={{
                        ...challanCard.statBlock(),
                        ...(isMobile && { minWidth: 60 }),
                    }}
                >
                    <div style={challanCard.statValue(s.color)}>{s.value}</div>
                    <div style={challanCard.statLabel}>{s.label}</div>
                </div>
            )),
        [stats, isMobile],
    );

    const safeTags = challan.tags ?? [];
    const safeTagColors = challan.tagColors ?? [];

    const renderTags = useMemo(
        () =>
            [0, 2].map((start) => (
                <div key={start} style={challanCard.tagRow}>
                    {safeTags.slice(start, start + 2).map((t, i) => {
                        const index = start + i;
                        return (
                            <Tag
                                key={index}
                                label={t}
                                colorKey={safeTagColors[index]}
                                icon={TAG_ICONS[index]?.(t) ?? null}
                            />
                        );
                    })}
                </div>
            )),
        [safeTags, safeTagColors],
    );

    // ── Body data ─────────────────────────────────────────────────────────────
    const body = bodyDetail ?? challan;

    const hasFlag = Boolean(body.flag);
    const hasTimeline = Array.isArray(body.timeline) && body.timeline.length > 0;
    const hasMetrics = body?.inDetailInfo != null && (
        body.inDetailInfo.billCount != null ||
        body.inDetailInfo.billAmt != null ||
        body.inDetailInfo.billQty != null ||
        body.inDetailInfo.dtoOutletVisitSummary?.totalOutlet != null ||
        body.inDetailInfo.dtoOutletVisitSummary?.totalCover != null
    );
    const hasDetails = body?.inDetailInfo && Object.keys(body?.inDetailInfo).length > 0;
    const hasProducts = Array.isArray(body?.inDetailInfo?.vanStockTransferDetail)
        && body?.inDetailInfo?.vanStockTransferDetail.length > 0;
    const hasBodyData =
        hasFlag || hasTimeline || hasMetrics || hasDetails || hasProducts;

    const isCancelDisabled =
        challan.statusNumber == 2 ||
        challan.statusNumber == 1 ||
        challan.statusNumber == 3;

    const isSettlementDisabled =
        challan.statusNumber == 2 ||
        challan.statusNumber == 0 ||
        challan.statusNumber == 3;

    // ── JSX ───────────────────────────────────────────────────────────────────
    return (
        <div style={challanCard.wrapper(open)}>
            {/* ── HEADER ───────────────────────────────────────────────────── */}
            <div style={headerStyle} onClick={toggleOpen}>
                {/* LEFT */}
                <div style={challanCard.headerLeft}>
                    <div style={challanCard.titleRow}>
                        <TypographyCompo style={challanCard.title}>
                            # {challan.id}
                        </TypographyCompo>

                        {!isCancelDisabled ? (
                            <div
                                title={
                                    isCancelDisabled
                                        ? pendingChallanCanBeEdit
                                        : editChallan
                                }
                                style={{
                                    ...challanCard.iconBtnVariant(
                                        isCancelDisabled ? challanEditDisabledBorderColor : challanEditIconColor,
                                        isCancelDisabled ? challanEditDisabledBgColor : challanEditIconBgColor
                                    ),
                                    cursor: isCancelDisabled ? "not-allowed" : "pointer",
                                    opacity: isCancelDisabled ? 0.7 : 1,
                                }}
                                onClick={isCancelDisabled ? undefined : handleEditClick}
                            >
                                <EditIcon size={25} />
                            </div>
                        ) : null}

                        {/* <StatusBadge status={challan.status} /> */}
                    </div>

                    <div style={challanCard.meta}>
                        {createdMsg + " :"} {challan.created}
                        {isMobile && <br />}
                        <br />
                        {assignedMsg + " :"} {challan.repName}
                        {isMobile && <br />}
                        <br />
                        {beatMsg + " :"} {challan.beat}
                    </div>
                </div>

                {/* CENTRE – tags */}
                <div style={headerCenterStyle}>
                    <div style={challanCard.tagGroup}>{renderTags}</div>
                </div>

                {/* STATS */}
                <div style={challanCard.headerRight} onClick={stopPropagation}>
                    {renderStats}
                </div>

                {/* ACTIONS — FIX #2: chevron moved outside this stopPropagation div */}
                <div style={challanCard.headerRightIcons} onClick={stopPropagation}>


                    <div
                        title={isCancelDisabled ? pendingChallanCanBeCancel : cancelChallan}
                        style={{
                            ...challanCard.iconBtnVariant(
                                isCancelDisabled ? challanCancelIconBgColor : challanCancelIconColor,
                                isCancelDisabled ? challanCancelDisabledBgColor : challanCancelIconBgColor
                            ),
                            cursor: isCancelDisabled ? "not-allowed" : "pointer",
                            opacity: isCancelDisabled ? 0.7 : 1,
                        }}
                        onClick={!isCancelDisabled ? rowCancel : undefined}
                    >
                        <CancelIcon size={30} />
                    </div>

                    <div
                        title={isSettlementDisabled ? activeChallanCanBeSettle : challanSettlement}
                        style={{
                            ...challanCard.iconBtnVariant(
                                isSettlementDisabled ? challanSettlementDisabledBorderColor : bluishWhiteShade,
                                isSettlementDisabled ? challanSettlementDisabledBgColor : tagBlueBgColor
                            ),
                            cursor: isSettlementDisabled ? "not-allowed" : "pointer",
                            opacity: isSettlementDisabled ? 0.7 : 1,
                        }}
                        onClick={isSettlementDisabled ? null : handleChallanClick}
                    >
                        <BillIcon size={30} />
                    </div>

                    <div
                        title={isCancelDisabled ? pendingChallanCanBeDelete : deleteChallan}
                        onClick={!isCancelDisabled ? rowDelete : undefined}
                        style={{
                            ...challanCard.iconBtnVariant(
                                isCancelDisabled ? challanCancelIconBgColor : challanCancelIconColor,
                                isCancelDisabled ? challanCancelDisabledBgColor : challanCancelIconBgColor
                            ),
                            cursor: isCancelDisabled ? "not-allowed" : "pointer",
                            opacity: isCancelDisabled ? 0.7 : 1,
                        }}

                    >
                        <DeleteIcon size={30} />
                    </div>

                    <div
                        title={downloadCSV}
                        style={{
                            ...challanCard.iconBtnVariant(
                                challanExcelIconColor,
                                challanExcelIconBgColor
                            ),
                        }}
                        onClick={rowExcelDownload}
                    >
                        <ExcelIcon size={30} />
                    </div>
                </div>

                {/* CHEVRON — FIX #2: now a sibling of the actions div, not a child */}
                <div style={challanCard.chevron(open)} onClick={toggleOpen}>
                    <ChevronIcon />
                </div>
            </div>

            {/* ── BODY — FIX #1: only mounted when open ────────────────────── */}
            {open && (
                <div style={challanCard.body}>
                    {loadingBody && (
                        <div
                            style={{
                                color: grayShadeWithSoftBlue,
                                fontSize: 13,
                                textAlign: "center",
                                padding: "12px 0",
                            }}
                        >
                            {loadingDetailsMsg}
                        </div>
                    )}

                    {!loadingBody && (
                        <>
                            {hasFlag && <FlagStrip flag={body.flag} />}

                            {hasTimeline && (
                                <>
                                    <div style={sectionLabel}>{challanLifeCycleTimelineMsg}</div>
                                    <Timeline steps={body.timeline} />
                                </>
                            )}

                            {hasMetrics && (() => {
                                const d = body.inDetailInfo;
                                const outletSummary = d.dtoOutletVisitSummary ?? {};
                                const totalOutlet = outletSummary.totalOutlet ?? 0;
                                const totalCover = outletSummary.totalCover ?? 0;
                                const totalMissed = totalOutlet - totalCover;
                                const coverBar = totalOutlet > 0
                                    ? Math.min((totalCover / totalOutlet) * 100, 100)
                                    : null;

                                const mismatchedValue = `₹${(
                                    (d.vanStockTransferDetail ?? []).reduce(
                                        (sum, item) => sum + ((item.outletRate ?? 0) * (item.secondaryDiffQty ?? 0)),
                                        0
                                    )
                                ).toFixed(2)}`;

                                const metricList = [
                                    {
                                        label: "Bill Count",
                                        value: d.billCount ?? 0,
                                        color: metricGreenColor,
                                        bar: null,
                                    },
                                    {
                                        label: "Bill Amount",
                                        value: `₹${(d.billAmt ?? 0).toFixed(2).toLocaleString("en-IN")}`,
                                        color: metricGreenColor,
                                        bar: null,
                                    },
                                    {
                                        label: "Bill Qty",
                                        value: `${d.billQty ?? 0} units`,
                                        color: metricGreenColor,
                                        bar: null,
                                    },
                                    {
                                        label: "Outlet Assigned",
                                        value: totalOutlet,
                                        color: metricBlueColor,
                                        bar: null,
                                        sub: "Total outlets assigned",
                                    },
                                    {
                                        label: "Outlet Visited",
                                        value: totalCover,
                                        color: metricBlueColor,
                                        bar: coverBar,
                                        sub: "Outlets covered",
                                    },
                                    {
                                        label: "Missed outlet",
                                        value: totalMissed,
                                        color: metricBlueColor,
                                        bar: null,
                                        sub: "Outlets not visited",
                                    },
                                    {
                                        label: "Mismatched Amount",
                                        value: mismatchedValue,
                                        color: metricBlueColor,
                                        bar: null,
                                        sub: "Amount variance",
                                    },
                                ];

                                return (
                                    <div style={metricGridStyle}>
                                        {metricList.map((m, i) => (
                                            <MetricCard key={i} metric={m} />
                                        ))}
                                    </div>
                                );
                            })()}

                            {hasDetails && <DetailCard detail={body.inDetailInfo} />}

                            {hasProducts && <ProductsTable products={body?.inDetailInfo?.vanStockTransferDetail} />}

                            {!hasBodyData && (
                                <div
                                    style={{
                                        color: grayShadeWithSoftBlue,
                                        fontSize: 13,
                                        textAlign: "center",
                                        padding: "12px 0",
                                    }}
                                >
                                    {noDetailsAvailableForThisChallanMsg}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = {
    showNotification,
    showLoador,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChallanCard);